import React from 'react';
import { X, Users, Calendar, Check, Trash2, Clock, Mail, User, Shield, Eye, Edit, Crown } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

interface CalendarInvitationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  invitations: any[];
  onAccept: (invitationId: string) => void;
  onDecline: (invitationId: string) => void;
  onRefresh: () => void;
}

const CalendarInvitationsModal: React.FC<CalendarInvitationsModalProps> = ({ 
  isOpen, 
  onClose, 
  invitations, 
  onAccept, 
  onDecline, 
  onRefresh 
}) => {
  const getPermissionIcon = (level: string) => {
    switch (level) {
      case 'viewer': return Eye;
      case 'editor': return Edit;
      case 'admin': return Crown;
      default: return Shield;
    }
  };

  const getPermissionColor = (level: string) => {
    switch (level) {
      case 'viewer': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400';
      case 'editor': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'admin': return 'text-purple-600 bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getPermissionText = (level: string) => {
    switch (level) {
      case 'viewer': return 'Görüntüleyici';
      case 'editor': return 'Düzenleyici';
      case 'admin': return 'Yönetici';
      default: return level;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Takvim Davetleri</h2>
              <p className="text-gray-600 dark:text-gray-400">
                {invitations.length} bekleyen davet
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onRefresh}
              className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              title="Yenile"
            >
              <Clock className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {invitations.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Davet yok</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Şu an için bekleyen takvim davetiniz bulunmuyor
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {invitations.map((invitation) => {
                const PermissionIcon = getPermissionIcon(invitation.permission_level);
                
                return (
                  <div key={invitation.id} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: invitation.calendars?.color || '#3B82F6' }}
                          />
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {invitation.calendars?.name}
                          </h3>
                          <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getPermissionColor(invitation.permission_level)}`}>
                            <PermissionIcon className="w-3 h-3" />
                            <span>{getPermissionText(invitation.permission_level)}</span>
                          </span>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <User className="w-4 h-4" />
                            <span>Davet eden: {invitation.profiles?.name}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <Mail className="w-4 h-4" />
                            <span>Email: {invitation.profiles?.email}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <Clock className="w-4 h-4" />
                            <span>
                              Gönderilme: {format(new Date(invitation.created_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <Calendar className="w-4 h-4" />
                            <span>
                              Son geçerlilik: {format(new Date(invitation.expires_at), 'dd MMMM yyyy', { locale: tr })}
                            </span>
                          </div>
                        </div>

                        {invitation.message && (
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                            <p className="text-sm text-blue-800 dark:text-blue-400">
                              <strong>Mesaj:</strong> "{invitation.message}"
                            </p>
                          </div>
                        )}

                        {/* Permission Details */}
                        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Bu yetki ile yapabilecekleriniz:</h4>
                          <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <li className="flex items-center space-x-2">
                              <Check className="w-4 h-4 text-green-500" />
                              <span>Takvim etkinliklerini görüntüleme</span>
                            </li>
                            {(invitation.permission_level === 'editor' || invitation.permission_level === 'admin') && (
                              <>
                                <li className="flex items-center space-x-2">
                                  <Check className="w-4 h-4 text-green-500" />
                                  <span>Yeni etkinlik oluşturma</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                  <Check className="w-4 h-4 text-green-500" />
                                  <span>Mevcut etkinlikleri düzenleme</span>
                                </li>
                              </>
                            )}
                            {invitation.permission_level === 'admin' && (
                              <>
                                <li className="flex items-center space-x-2">
                                  <Check className="w-4 h-4 text-green-500" />
                                  <span>Etkinlikleri silme</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                  <Check className="w-4 h-4 text-green-500" />
                                  <span>Başka kullanıcıları davet etme</span>
                                </li>
                              </>
                            )}
                          </ul>
                        </div>
                      </div>
                      
                      <div className="flex flex-col space-y-2 ml-4">
                        <button
                          onClick={() => onAccept(invitation.id)}
                          className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          <span>Kabul Et</span>
                        </button>
                        <button
                          onClick={() => onDecline(invitation.id)}
                          className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                          <span>Reddet</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarInvitationsModal;